import { Box, Chip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import IconAvatar from 'components/icon-avatar/icon-avatar.component';
import EventTimelineEntryNote from './event-timeline-entry-note/event-timeline-entry-note.component';
import { EventTimelineEntry as EventTimelineEntryType } from '../event-timeline.types';
import { parseISODateToLocaleString } from 'util/luxon-utils';

type EventTimelineEntryProps = {
  entry: EventTimelineEntryType;
  isLast: boolean;
  /**
   * Named per row where a list needs to tell its rows apart. The connector and
   * the label hang off it, so a caller that passes one can reach both.
   */
  'data-testid'?: string;
};

const EventTimelineEntry = ({
  entry,
  isLast,
  'data-testid': dataTestId = 'EventTimelineEntry',
}: EventTimelineEntryProps) => {
  const { t } = useTranslation();
  const { visual, date, author, detail, note, footer } = entry;

  return (
    <Box display="flex" gap={1.75} data-testid={dataTestId}>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        flexShrink={0}
      >
        <IconAvatar severity={visual.severity} width="28px" height="28px">
          {visual.icon}
        </IconAvatar>
        {!isLast && (
          <Box
            data-testid={`${dataTestId}.Connector`}
            flex={1}
            width="2px"
            bgcolor="divider"
            mt={0.75}
            mb={0.75}
            minHeight="18px"
          />
        )}
      </Box>
      <Box flex={1} minWidth={0} pb={isLast ? 0 : 2.75}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          mb={0.75}
        >
          <Chip
            label={visual.label}
            color={visual.severity}
            size="small"
            data-testid={`${dataTestId}.Label`}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            whiteSpace="nowrap"
          >
            {parseISODateToLocaleString(date)}
          </Typography>
        </Box>
        {(author || detail) && (
          <Box
            display="flex"
            alignItems="baseline"
            gap={1}
            flexWrap="wrap"
            mb={note || footer ? 1 : 0}
          >
            {author && (
              <Typography variant="body2">
                {t('EventTimeline.byAuthor', { author })}
              </Typography>
            )}
            {detail && (
              <Typography variant="caption" color="text.disabled">
                {detail}
              </Typography>
            )}
          </Box>
        )}
        {note && <EventTimelineEntryNote note={note} />}
        {footer && <Box mt={note ? 1 : 0}>{footer}</Box>}
      </Box>
    </Box>
  );
};

export default EventTimelineEntry;
